/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import SelectFromRest from '../../../components/Form/SelectFromRest';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { specializedReportConstants } from '../../../modules/specializedReports/reducers';
import { SpecializedReportTypeCollectionInterface } from '../../../interfaces/collections/SpecializedReportTypeCollectionInterface';
import { useDispatch } from 'react-redux';
import { Moment } from 'moment';
import InputFormRest from '../../../components/Form/InputFormRest';
import CustomSmallTextButton from '../../../components/Buttons/CustomSmallTextButton';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import { platformResellerConstants } from '../../../modules/platformreseller/actions';
import { SimCardMnoTariffLoader } from '../../../modules/tariffs/SimCardMnoTariffLoader';
import { MnpEventType } from '../../Mnp/MnpEventType';
import { fractionsConstants } from '../../../modules/fractions/actions';
import { FractionCollectionInterface } from '../../../interfaces/collections/FractionCollectionInterface';
import PlatformResellerInterface from '../../../interfaces/platform/PlatformResellerInterface';
import { Fraction } from '../../../interfaces/platform/Fraction';
import CustomDatePicker from '../../../components/Form/CustomDatePicker';
import TariffForm from './ReportsForm/TariffForm';
import FractionForm from './ReportsForm/FractionForm';
import MnpEventForm from './ReportsForm/MnpEventForm';
import IccidForm from './ReportsForm/IccidForm';
import OnlyPlatformResellerMultiForm from './ReportsForm/OnlyPlatformResellerMultiForm';
import OnlyPlatformResellerForm from './ReportsForm/OnlyPlatformResellerForm';

interface SpecializedReportFormProps {
    generateReport: (param: { entity: any }) => void;
}

const SpecializedReportForm = (props: SpecializedReportFormProps): React.ReactElement => {
    const dispatch = useDispatch();

    const mnpEventType = new MnpEventType();
    const mnpTypes = { 'hydra:member': mnpEventType.getEventTypes() };
    const [platformResellerList, setPlatformResellerList] = useState<PlatformResellerCollectionInterface | null>(null);
    const [specializedReportList, setSpecializedReportList] = useState<SpecializedReportTypeCollectionInterface | null>(null);
    const [selectedSpecializedReportName, setSelectedSpecializedReportName] = useState<any>(null);
    const [tariffPlanList, setTariffPlanList] = useState<{ 'hydra:member': Array<object> } | null>(null);
    const [fractionsList, setFractionsList] = useState<FractionCollectionInterface | null>(null);
    const [commandId, setCommandId] = useState<number | null>(null);
    const [typeId, setTypeId] = useState<number | null>(null);
    const [formSettings, setFormSettings] = useState<any>(null);
    const [formParams, setFormParams] = useState<any>(null);
    const [entity, setEntity] = useState<any>({
        commandId: null,
        limit: null,
        dateStart: DateHelperTs.format(DateHelperTs.getCurentDate(), 'YYYY-MM-DD 00:00:00'),
        dateEnd: DateHelperTs.format(DateHelperTs.getCurentDate(), 'YYYY-MM-DD 23:59:59'),
        platformResellerId: [],
        tariffPlanId: [],
        mnpEventType: [],
        fractionId: [],
        iccidFrom: null,
        iccidTo: null,
    });
    const SPECIALIZED_REPORT_TYPE_URI = '/specialized_report_types';
    const PLATFORM_RESELLER_URI = '/platform_resellers';

    const firstLoad = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(SPECIALIZED_REPORT_TYPE_URI, configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                specializedReportConstants.LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [],
                (data: SpecializedReportTypeCollectionInterface) => {
                    setSpecializedReportList(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    const loadPlatformResellers = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PLATFORM_RESELLER_URI, configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformResellerConstants.PLATFORM_RESELLER_LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [],
                (data: PlatformResellerCollectionInterface) => {
                    setPlatformResellerList(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    const selectSpecializedReport = (event: any, stateName: any): void => {
        let selectedReport = null;
        let report: any = null;

        if (specializedReportList && specializedReportList['hydra:member']) {
            specializedReportList['hydra:member'].forEach((el: any) => {
                if (el.id === event.target.value) {
                    selectedReport = el.mainname;
                    report = el;
                }
            });
            let newEntity = {
                ...entity,
                commandId: event.target.value,
            };
            setCommandId(event.target.value);
            setTypeId(report.reportType);
            setSelectedSpecializedReportName(selectedReport);
            setFormSettings(JSON.parse(report ? report.formSettings : '{}'));
            setFormParams(JSON.parse(report ? report.formParams : '{}'));
            setEntity(newEntity);
        }
    };

    const handleChangeMultiple = (event: any): void => {
        const { value, name } = event.target;
        let newEntity = { ...entity, [name]: value };
        setEntity(newEntity);
    };

    const selectDates = (date: Moment | string, fieldName: string, endDate = false): void => {
        let newDate = '';
        if (typeof date === 'object') {
            newDate = date.format('YYYY-MM-DD HH:mm:ss');
        } else {
            newDate = date;
        }

        const oldTime = DateHelperTs.format(entity[fieldName], 'HH:mm:ss');
        const newTime = DateHelperTs.format(newDate, 'HH:mm:ss');

        let formattedDate = DateHelperTs.format(newDate, 'YYYY-MM-DD HH:mm:ss');
        if (oldTime === newTime) {
            if (endDate) {
                formattedDate = DateHelperTs.format(newDate, 'YYYY-MM-DD 23:59:59');
            } else {
                formattedDate = DateHelperTs.format(newDate, 'YYYY-MM-DD 00:00:00');
            }
        }

        setEntity({
            ...entity,
            [fieldName]: formattedDate,
        });
    };

    const changeLimit = (event: any, stateName: string, typeCallback: string, required: boolean, vMax: number, vMin: number): void => {
        setEntity({
            ...entity,
            limit: event.target.value,
        });
    };

    const changeInput = (event: any, stateName: string): void => {
        let newEntity = { ...entity, [stateName]: event.target.value };
        setEntity(newEntity);
    };

    useEffect(() => {
        firstLoad();
    }, [firstLoad]);

    useEffect(() => {
        loadPlatformResellers();
    }, [loadPlatformResellers]);

    useEffect(() => {
        const LOADER = new ItemLoaders(dispatch);
        const simCardMnoTariffLoader = new SimCardMnoTariffLoader(LOADER);

        simCardMnoTariffLoader.load({
            callback: () => {
                setTariffPlanList({ 'hydra:member': simCardMnoTariffLoader.getHeaderList() });
            },
            orders: [{ name: 'constName', value: 'asc' }],
            filters: [{ name: 'typeValue', value: 'TARIFF' }],
        });
    }, [dispatch]);

    useEffect(
        (uri?: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath('/fractions', configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                fractionsConstants.LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [{ name: 'mainname', value: 'asc' }],
                (data: FractionCollectionInterface) => {
                    setFractionsList(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    useEffect(() => {
        if (fractionsList && entity.fractionId.length === 0) {
            setEntity({ ...entity, fractionId: fractionsList['hydra:member'].map((el: Fraction): number => el.id) });
        }
    }, [fractionsList, entity]);

    useEffect(() => {
        if (tariffPlanList && entity.tariffPlanId.length === 0) {
            setEntity({ ...entity, tariffPlanId: tariffPlanList['hydra:member'].map((el: any) => el.value) });
        }
    }, [tariffPlanList, entity]);

    useEffect(() => {
        if (mnpTypes && entity.mnpEventType.length === 0) {
            setEntity({ ...entity, mnpEventType: mnpTypes['hydra:member'].map((el: any) => el.value) });
        }
    }, [mnpTypes, entity]);

    useEffect(() => {
        if (platformResellerList && platformResellerList['hydra:member'].length > 0 && entity.platformResellerId.length !== 1 && commandId === 9) {
            setEntity({ ...entity, platformResellerId: [platformResellerList['hydra:member'][0].id] });
        }
    }, [platformResellerList, entity, commandId]);

    return (
        <Card>
            <CardHeader icon={<AssessmentIcon />} trans="specialized_report:h.form" />
            <CardBody>
                <GridContainer>
                    <GridItem xs={12}>
                        <GridContainer spacing={1}>
                            <GridItem xs={3}>
                                <SelectFromRest
                                    callback={selectSpecializedReport}
                                    required={true}
                                    addEmpty={false}
                                    restElements={specializedReportList}
                                    stateName={'commandId'}
                                    stateObjectState={'commandId'}
                                    selectedValue={entity.commandId}
                                    propKey="id"
                                    propValue="mainname"
                                    displayEmpty={true}
                                    emptyLabel={'specialized_report:h.select_missing'}
                                    label={'specialized_report:h.select'}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12}>
                        <GridContainer spacing={1}>
                            {formSettings && formSettings.displayResellerSimpleSelect && (
                                <OnlyPlatformResellerForm
                                    platformResellerList={platformResellerList}
                                    platformResellerId={entity.platformResellerId}
                                    handleChangeMultiple={handleChangeMultiple}
                                />
                            )}
                            {formSettings && formSettings.displayResellerMultiSelect && (
                                <OnlyPlatformResellerMultiForm
                                    platformResellerList={platformResellerList}
                                    platformResellerId={entity.platformResellerId}
                                    handleChangeMultiple={handleChangeMultiple}
                                />
                            )}
                            {formSettings && formSettings.displayTariffSelect && (
                                <TariffForm
                                    tariffPlanList={tariffPlanList}
                                    tariffPlanId={entity.tariffPlanId}
                                    handleChangeMultiple={handleChangeMultiple}
                                />
                            )}
                            {formSettings && formSettings.displayFractionSelect && (
                                <FractionForm
                                    fractionsList={fractionsList}
                                    fractionId={entity.fractionId}
                                    handleChangeMultiple={handleChangeMultiple}
                                />
                            )}
                            {formSettings && formSettings.displayMnpEventSelect && (
                                <MnpEventForm handleChangeMultiple={handleChangeMultiple} mnpTypes={mnpTypes} mnpEventType={entity.mnpEventType} />
                            )}
                            {formSettings && formSettings.displayIccidSelect && (
                                <IccidForm
                                    changeInput={changeInput}
                                    handleChangeMultiple={handleChangeMultiple}
                                    iccidFrom={entity.iccidFrom}
                                    iccidTo={entity.iccidTo}
                                />
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12}>
                        {entity.commandId && (
                            <GridContainer spacing={1}>
                                <GridItem xs={4}>
                                    <div>
                                        <CustomDatePicker
                                            helpText={'specialized_report:h.from'}
                                            fieldName={'dateStart'}
                                            callback={(moment: Moment | string): void => selectDates(moment, 'dateStart')}
                                            dateFormat={'YYYY-MM-DD'}
                                            autoComplete={'off'}
                                            defaultValue={entity.dateStart}
                                            timeFormat={'HH:mm:ss'}
                                        />
                                    </div>
                                </GridItem>
                                <GridItem xs={4}>
                                    <div>
                                        <CustomDatePicker
                                            helpText={'specialized_report:h.to'}
                                            fieldName={'dateEnd'}
                                            callback={(moment: Moment | string): void => selectDates(moment, 'dateEnd', true)}
                                            dateFormat={'YYYY-MM-DD'}
                                            autoComplete={'off'}
                                            defaultValue={entity.dateEnd}
                                            timeFormat={'HH:mm:ss'}
                                        />
                                    </div>
                                </GridItem>
                                {(!formSettings || !{}.propertyIsEnumerable.call(formSettings, 'hideLimit') || formSettings.hideLimit !== true) && (
                                    <GridItem xs={4}>
                                        <InputFormRest
                                            callback={changeLimit}
                                            stateName={'limit'}
                                            stateObjectState={''}
                                            typeCallback={FormGeneralHelper.TYPE_TEXT}
                                            typeField={FormGeneralHelper.TYPE_TEXT}
                                            displayFieldName={'specialized_report:h.limit'}
                                            vMax={256}
                                            vMin={-1}
                                            required={true}
                                            value={entity.limit}
                                        />
                                    </GridItem>
                                )}
                            </GridContainer>
                        )}
                    </GridItem>
                    <GridItem xs={12}>
                        {entity.commandId !== null && entity.dateStart !== null && entity.dateEnd !== null && (
                            <CustomSmallTextButton
                                callback={props.generateReport}
                                callbackParam={{
                                    entity: entity,
                                    reportName: selectedSpecializedReportName,
                                    typeId: typeId,
                                    formSettings: formSettings,
                                }}
                                type="button"
                                label={'specialized_report:b.generate_raport'}
                            />
                        )}
                    </GridItem>
                </GridContainer>
            </CardBody>
        </Card>
    );
};

export default SpecializedReportForm;
